import type {
  PanderrRenderedTemplate,
  PanderrTemplateDefinition,
  PanderrTemplatePage,
} from "./types";

import { createPanderrBaseCss } from "./base-css";
import { createHeroSectionCss, renderHeroSection } from "./sections/hero";

import {
  createBodyCss,
  renderTrustSection,
  renderServicesSection,
  renderAboutSection,
  renderCtaSection,
  renderProcessSection,
  renderProjectsSection,
  renderTestimonialsSection,
  renderServiceAreaSection,
  renderContactSection,
  renderGallerySection,
  renderFaqSection,
} from "./sections/body";

import { createHeaderCss, renderPanderrHeader } from "./components/header";

import { createFooterCss, renderPanderrFooter } from "./components/footer";

function renderSection(
  section: PanderrTemplateDefinition["sections"][number],
): string {
  switch (section.type) {
    case "hero":
      return renderHeroSection(section);

    case "trust":
      return renderTrustSection(section);

    case "services":
      return renderServicesSection(section);

    case "about":
      return renderAboutSection(section);

    case "cta":
      return renderCtaSection(section);

    case "process":
      return renderProcessSection(section);

    case "projects":
      return renderProjectsSection(section);

    case "testimonials":
      return renderTestimonialsSection(section);

    case "service-area":
      return renderServiceAreaSection(section);

    case "contact":
      return renderContactSection(section);

    case "gallery":
      return renderGallerySection(section);

    case "faq":
      return renderFaqSection(section);

    default:
      return "";
  }
}

function createPanderrTemplateCss(): string {
  return [
    createPanderrBaseCss(),
    createHeaderCss(),
    createHeroSectionCss(),
    createBodyCss(),
    createFooterCss(),
  ].join("\n");
}

export function renderPanderrTemplate(
  template: PanderrTemplateDefinition,
): PanderrRenderedTemplate {
  const html = template.sections.map(renderSection).join("\n");

  const css = createPanderrTemplateCss();

  return {
    html: `
      <main
        data-panderr-design-system="v1"
        data-panderr-template-id="${template.id}"
      >
        ${html}
      </main>
    `,
    css,
  };
}

export function renderPanderrTemplatePage(
  template: PanderrTemplateDefinition,
  page: PanderrTemplatePage,
): PanderrRenderedTemplate {
  const html = page.sections.map(renderSection).join("\n");

  return {
    html: `
      <main
        data-panderr-design-system="v1"
        data-panderr-template-id="${template.id}"
        data-panderr-template-page-id="${page.id}"
      >
        ${html}
      </main>
    `,
    css: createPanderrTemplateCss(),
  };
}

export function renderPanderrPageDocument(
  template: PanderrTemplateDefinition,
  page: PanderrTemplatePage,
): string {
  const rendered = renderPanderrTemplatePage(template, page);

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  >
  <title>${page.title}</title>
  <style>
${rendered.css}
  </style>
</head>
<body>
${renderPanderrHeader(template)}
${rendered.html}
${renderPanderrFooter(template)}
</body>
</html>`;
}

export function renderPanderrDocument(
  template: PanderrTemplateDefinition,
): string {
  const rendered = renderPanderrTemplate(template);

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  >
  <title>${template.name}</title>
  <style>
${rendered.css}
  </style>
</head>
<body>
${renderPanderrHeader(template)}
${rendered.html}
${renderPanderrFooter(template)}
</body>
</html>`;
}
